/**
 * Learn tab: path map of the current path, path switcher panel, coin pieces (warm-up path),
 * weekly challenge / draw together / end-of-path side widgets.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COIN_PIECES, FTUE_DRAWINGS_TO_UNLOCK_LEARN, WEEKLY_CHALLENGE_TARGET, activeChallenge, isNodeDone, isPathOpen, markSeen, nextPathLesson, pathFraction, type NodeStatus, type PathConfig, type PathId, type PathUnitItem } from '@sketchling/core';
import { appNow, useAccess, useActiveProfile, useApp, useProgress } from '../../state/store';
import { wishPath } from '../wish/wish';
import { IX, drawingById, CONTENT } from '../../lib/content';
import { useLoc, useT } from '../../lib/i18n';
import { play } from '../../lib/sfx';
import { PathMap } from '../../components/PathMap';
import { DrawingCardArt } from '../../components/DrawingView';
import { Icon } from '../../components/icons';
import { Button, Modal, ProgressBar, toast } from '../../components/ui';
import { ShelfRow } from '../../components/ShelfRow';
import { LockedState, MainHeader, useCountdown, useNow } from './common';
import { DrawTogetherBanner } from '../../components/DrawTogetherBanner';
import { Crossroads } from '../../components/Crossroads';

export function Learn() {
  const t = useT();
  const loc = useLoc();
  const nav = useNavigate();
  const p = useProgress();
  const access = useAccess();
  const profile = useActiveProfile();
  const updateProgress = useApp((s) => s.updateProgress);
  const cfg = useApp((s) => s.config);
  const [panel, setPanel] = useState(false);
  const path = IX.paths.get(p.currentPath) ?? CONTENT.paths[0]!;
  const showHand = !p.seen.includes('learnTippingHand');
  const complete = p.completedPaths.includes(path.id) || pathFraction(p, path) >= 1;
  const next = nextPathLesson(p, path);

  const onNodeTap = (item: PathUnitItem, st: NodeStatus) => {
    if (profile && showHand) updateProgress(profile.id, (x) => markSeen(x, 'learnTippingHand', appNow()));
    if (st === 'current' || st === 'done') {
      play('click');
      nav(`/lesson/${item.miniUnitId}?from=/learn`);
    } else if (st === 'premium') {
      play('click');
      // Monetization P0-4: the child gets the wish screen (no price); grown-ups decide behind the gate.
      nav(wishPath('node', item.miniUnitId ?? item.id, '/learn'));
    } else {
      play('disabled');
      if (item.branch === 'picture') {
        // Picture tier: open once the object lesson is done AND the world reached level `pictureMinLevel`.
        const obj = path.units.flatMap((u) => u.items).find((i) => i.id === item.pictureOf);
        const objMu = obj?.miniUnitId ? IX.miniUnits.get(obj.miniUnitId) : undefined;
        const n = access.pictureMinLevel;
        toast(obj && isNodeDone(p, obj) ? t('v2.picLevel', { n }) : t('v2.picLocked', { name: obj?.title ? loc(obj.title) : objMu ? loc(objMu.title) : '', n }));
        return;
      }
      toast(st === 'keyLocked' ? t('learn.needKey') : t('learn.finishPrevious'));
    }
  };

  const choosePath = (id: PathId) => {
    if (!profile) return;
    updateProgress(profile.id, (x) => ({ ...x, currentPath: id, updatedAt: appNow().toISOString() }));
    setPanel(false);
    play('whoosh');
  };

  if (p.ftue.stage === 'playground' && !access.unlockAll)
    return (
      <LockedState
        title={t('learn.lockedTitle')}
        body={t('learn.lockedBody', { n: Math.max(0, FTUE_DRAWINGS_TO_UNLOCK_LEARN - p.ftue.drawings) })}
        cta={t('nav.playground')}
        onCta={() => nav('/playground')}
        testId="learn-locked"
      />
    );

  return (
    <div className="learn" data-testid="learn-screen">
      <MainHeader
        testId="learn-header"
        title={
          <button type="button" className="path-switch" onClick={() => (play('drawer'), setPanel(true))} data-testid="switch-path" aria-label={t('learn.switchPath')}>
            <span className="muted">{t('nav.learn')} ·</span> <b>{loc(path.title)}</b> <Icon name="forward" size={18} className="rot90" />
          </button>
        }
      />
      <div className="learn-body">
        <PathMap path={path} progress={p} access={access} showHand={showHand} onNodeTap={onNodeTap} />
        <aside className="learn-side">
          {path.id === 'intro' && (!complete || p.ftue.stage !== 'done') && <CoinCard />}
          {complete && <EndOfPathCard path={path} onPick={choosePath} />}
          {cfg.enableWeeklyChallenges && <ChallengeBanner />}
          {cfg.enableDrawTogether && next?.miniUnitId && p.ftue.stage === 'done' && <DrawTogetherBanner miniUnitId={next.miniUnitId} from="/learn" />}
        </aside>
      </div>
      {panel && <PathPanel current={path.id} onPick={choosePath} onClose={() => setPanel(false)} />}
    </div>
  );
}

/* ------------------------------- Path selection ------------------------------- */

function PathPanel({ current, onPick, onClose }: { current: PathId; onPick: (id: PathId) => void; onClose: () => void }) {
  const t = useT();
  const loc = useLoc();
  const p = useProgress();
  const access = useAccess();
  const promoted = useMemo(() => {
    const cur = IX.paths.get(current);
    return cur?.suggestNext.find((id) => !p.completedPaths.includes(id) && id !== current) ?? null;
  }, [current, p.completedPaths]);
  // The promoted ("try next") card is highlighted with its own chime when the panel opens.
  useEffect(() => {
    if (promoted) play('highlight');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Modal onClose={onClose} wide label={t('learn.choosePath')} testId="path-panel">
      <h2 className="ft-modal-title">{t('learn.choosePath')}</h2>
      <ShelfRow kind="worlds" trackClassName="path-grid path-shelf" label={t('learn.choosePath')} seeAll={false} testId="path-shelf">
        {CONTENT.paths.map((path) => {
          const open = isPathOpen(p, path.id, access);
          const premium = !access.premium && path.id !== 'intro';
          const frac = pathFraction(p, path);
          const hero = drawingById(path.heroDrawingId);
          return (
            <button
              key={path.id}
              type="button"
              className={`lpath-card c-${path.color}${path.id === current ? ' current' : ''}${open ? '' : ' locked'}`}
              data-testid={`path-card-${path.id}`}
              aria-label={`${loc(path.title)}${open ? '' : ` (${t('locked')})`}`}
              onClick={() => {
                if (!open) {
                  play('disabled');
                  toast(t('learn.finishWarmup'));
                  return;
                }
                onPick(path.id);
              }}
            >
              <div className="pcard-hero">{hero && <DrawingCardArt drawing={hero} />}</div>
              <div className="pcard-body">
                <b>{loc(path.title)}</b>
                <ProgressBar value={frac} tone={frac >= 1 ? 'success' : undefined} />
                <small className="muted">{open ? (frac >= 1 ? t('learn.pathDone') : t('learn.percent', { n: Math.round(frac * 100) })) : t('learn.finishWarmup')}</small>
              </div>
              {!open && (
                <span className="pcard-lock">
                  <Icon name="lock" size={18} />
                </span>
              )}
              {open && premium && (
                <span className="pcard-crown" aria-label={t('premium')}>
                  <Icon name="crown" size={16} />
                </span>
              )}
              {promoted === path.id && open && <span className="pcard-ribbon">{t('learn.tryNext')}</span>}
              {path.id === current && <span className="pcard-current">{t('learn.youAreHere')}</span>}
            </button>
          );
        })}
      </ShelfRow>
      <Crossroads onOpen={onClose} />
    </Modal>
  );
}

/* ---------------------------------- Widgets ---------------------------------- */

export function CoinGlyph({ pieces, size = 64 }: { pieces: readonly string[]; size?: number }) {
  // Three wedges of one coin: outline (top), pattern (bottom-left), shading (bottom-right).
  const wedge = (a0: number, a1: number) => {
    const r = 28;
    const p0 = [32 + r * Math.cos(a0), 32 + r * Math.sin(a0)];
    const p1 = [32 + r * Math.cos(a1), 32 + r * Math.sin(a1)];
    return `M32 32L${p0[0]} ${p0[1]}A${r} ${r} 0 0 1 ${p1[0]} ${p1[1]}z`;
  };
  const A = [-Math.PI / 2 - (Math.PI * 2) / 6, -Math.PI / 2 + (Math.PI * 2) / 6, Math.PI / 2, -Math.PI / 2 - (Math.PI * 2) / 6 + Math.PI * 2];
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className="coin-glyph" aria-hidden>
      {COIN_PIECES.map((c, i) => {
        const has = pieces.includes(c);
        return <path key={c} d={wedge(A[i]!, A[i + 1]!)} fill={has ? '#FFC23A' : 'rgba(255,251,244,0.08)'} stroke={has ? '#E08A00' : 'rgba(255,251,244,0.35)'} strokeWidth="2" strokeDasharray={has ? undefined : '4 3'} />;
      })}
      <circle cx="32" cy="32" r="9" fill={pieces.length === 3 ? '#FF7A1A' : 'none'} stroke={pieces.length === 3 ? '#E08A00' : 'rgba(255,251,244,0.35)'} strokeWidth="2" />
    </svg>
  );
}

function CoinCard() {
  const t = useT();
  const p = useProgress();
  const missing = COIN_PIECES.length - p.coinPieces.length;
  return (
    <div className="side-card coin-card" data-testid="coin-card">
      <CoinGlyph pieces={p.coinPieces} size={58} />
      <div className="grow">
        <b>{t('learn.coinTitle')}</b>
        <small className="muted">{missing > 0 ? t('learn.coinMissing', { n: missing }) : t('learn.coinDone')}</small>
        <div className="lc-coin-labels">
          {COIN_PIECES.map((c) => (
            <span key={c} className={p.coinPieces.includes(c) ? 'on' : ''}>
              {t(`learn.coin.${c}`)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChallengeBanner() {
  const t = useT();
  const loc = useLoc();
  const nav = useNavigate();
  const p = useProgress();
  const now = useNow();
  const fmt = useCountdown();
  const a = activeChallenge(CONTENT.challenges, now);
  if (!a) return null;
  const n = p.weekly[a.key]?.entries.length ?? 0;
  return (
    <button type="button" className="side-card challenge-banner" onClick={() => (play('click'), nav('/challenges'))} data-testid="challenge-banner">
      <span className="cb-icon">
        <Icon name="trophy" />
      </span>
      <div className="grow">
        <small className="caps">{t('wc.weekly')}</small>
        <b>{loc(a.challenge.title)}</b>
        <div className="cb-slots">
          {Array.from({ length: WEEKLY_CHALLENGE_TARGET }, (_, i) => (
            <i key={i} className={i < n ? 'on' : ''} />
          ))}
          <small className="muted">{t('wc.endsIn', { time: fmt(a.endsAt, now) })}</small>
        </div>
      </div>
    </button>
  );
}

function EndOfPathCard({ path, onPick }: { path: PathConfig; onPick: (id: PathId) => void }) {
  const t = useT();
  const loc = useLoc();
  return (
    <div className="side-card end-card" data-testid="end-of-path">
      <b>{t('learn.endTitle', { path: loc(path.title) })}</b>
      <small className="muted">{t('learn.endBody')}</small>
      <div className="row wrap">
        {path.suggestNext.map((id) => {
          const sp = IX.paths.get(id);
          return sp ? (
            <Button key={id} small variant="secondary" onClick={() => onPick(id)}>
              {loc(sp.title)}
            </Button>
          ) : null;
        })}
      </div>
    </div>
  );
}
